import { Loader } from "@circle-react-uikit/Loader";
import { Modal } from "@circle-react-uikit/ModalV2";
import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { LoaderRow } from "@circle-react/components/shared/LoaderRow";
import { toLocaleString } from "@circle-react/helpers/number";
import type { Comment } from "@circle-react/types/Comment";
import InfiniteScroll from "react-infinite-scroll-component";

import { t } from "@/i18n-js/instance";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";

import { MemberRow } from "./MemberRow";
import { useLikesList } from "./useLikesList";

interface LikesModalUIKitComponentProps {
  comment: Comment;
}

export const LikesModalUIKitComponent = ({
  comment,
}: LikesModalUIKitComponentProps) => {
  const modal = useModal();

  const { members, totalCount, isInitialLoading, hasNextPage, fetchNextPage } =
    useLikesList({ comment });
  const likesCount = totalCount ?? comment.likes_count;

  const title = t("comments.likes", {
    count: likesCount,
    formatted_count: toLocaleString(likesCount),
  });

  return (
    <Modal title={title} isOpen={modal.visible} onClose={modal.hide}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title size="sm">
            <Typography.LabelLg weight="semibold">{title}</Typography.LabelLg>
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={modal.hide} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="pb-6 pt-4">
          {isInitialLoading ? (
            <LoaderRow />
          ) : (
            <div
              id={`comment-likes-modal-${comment.id}`}
              // height would be 100vh - modal header height - padding of the modal body
              className="max-h-[calc(100dvh-62px-44px-24px)] overflow-auto sm:max-h-80"
            >
              <InfiniteScroll
                scrollThreshold={0.75}
                next={fetchNextPage}
                hasMore={hasNextPage ?? false}
                loader={<Loader />}
                scrollableTarget={`comment-likes-modal-${comment.id}`}
                dataLength={members.length}
                className="flex flex-col space-y-4"
              >
                {members.map(communityMember => (
                  <MemberRow
                    key={communityMember.id}
                    communityMember={communityMember}
                    onNavigate={modal.hide}
                  />
                ))}
              </InfiniteScroll>
            </div>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
};
